import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSpinner } from '@/icons';
import { Modal } from './Modal';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useSimpleCaseContext } from '../contexts/SimpleCaseContext/SimpleCaseContextDef';

/**
 * MergePendingModal — shown when the server reports pending submissions (e.g. client-submitted
 * expenses) that the user hasn't merged yet.
 *
 * The merge round-trip can replace large portions of the case (notably Financial.ExpenseGroups),
 * so the user is asked to opt in explicitly. While the call is in flight, the modal blocks
 * dismissal and the user is prevented from editing form fields (the modal sits over the page).
 * Only on success is formState.data overwritten — the only sanctioned path for that.
 */
export const MergePendingModal: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { pendingServerChanges, triggerMerge, dismissPendingChanges, isMerging } = useSimpleCaseContext();

    if (!pendingServerChanges) {
        return null;
    }

    const handleMerge = async () => {
        try {
            await triggerMerge();
        } catch {
            // Errors already surfaced via addAlert in triggerMerge; keep modal open so user can retry.
        }
    };

    const handleNotYet = () => {
        if (isMerging) return; // cannot dismiss mid-merge
        dismissPendingChanges(); // re-surfaces on the next autosave while still pending
    };

    const isClientExpenses = pendingServerChanges.kind === 'clientExpenses';
    const bodyText = isClientExpenses
        ? 'Your client has submitted their expenses. Importing pulls them into your copy of this case and re-saves. Their entries replace the current expense entries; anything else you have unsaved on this page is preserved.'
        : 'The server has changes for this case that have not been applied to your local copy.';
    const modalTitle = isClientExpenses ? 'Client submitted expenses' : 'Pending updates available';
    const headlineText = isClientExpenses ? 'Client submitted expenses' : 'Server-side changes detected';
    const mergeLabel = isClientExpenses ? 'Import now' : 'Merge now';
    const mergingLabel = isClientExpenses ? 'Importing...' : 'Merging...';

    return (
        <Modal
            isOpen={true}
            onClose={handleNotYet}
            title={modalTitle}
            headerBackgroundColor={theme.colors.primary}
            showCloseButton={false}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.md,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    backgroundColor: '#fef3c7',
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.status.warning || '#f59e0b'}`,
                }}>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{
                            fontSize: theme.typography.fontSize.lg,
                            color: '#92400e',
                            marginTop: theme.spacing.xs,
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#92400e',
                            fontWeight: theme.typography.fontWeight.medium,
                            margin: `0 0 ${theme.spacing.xs} 0`,
                        }}>
                            {headlineText}
                        </p>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#92400e',
                            fontSize: theme.typography.fontSize.sm,
                            margin: 0,
                        }}>
                            {bodyText}
                        </p>
                    </div>
                </div>

                {isMerging && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.md,
                        color: theme.colors.text?.primary || '#111827',
                    }}>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Merging — please wait. Do not close this dialog.</span>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: theme.spacing.sm,
                    marginTop: theme.spacing.md,
                }}>
                    <button
                        type="button"
                        onClick={handleNotYet}
                        disabled={isMerging}
                        style={{
                            ...themedStyles.button.secondary,
                            opacity: isMerging ? 0.5 : 1,
                            cursor: isMerging ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Not Yet
                    </button>
                    <button
                        type="button"
                        onClick={handleMerge}
                        disabled={isMerging}
                        style={{
                            ...themedStyles.button.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.xs,
                            opacity: isMerging ? 0.7 : 1,
                            cursor: isMerging ? 'wait' : 'pointer',
                        }}
                    >
                        {isMerging ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {mergingLabel}
                            </>
                        ) : (
                            mergeLabel
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
